/**
 * Site Vault Card (office — CustomerDetailModal)
 *
 * Editable store for a customer's SITE passwords / access credentials:
 * WiFi (SSID + password) plus an arbitrary list of labelled credentials
 * (gate code, alarm code, router admin, NBN/ISP portal login, etc.).
 *
 * This is the canonical, office-managed vault that lives on the customer
 * record (`customer.vault`). Techs see a read-only, tap-to-reveal copy of
 * it on their job card (see CredentialsModule), so anything entered here
 * follows the customer to every job at the site.
 *
 * Recovery path: a tech who enters WiFi at job completion writes it to the
 * JOB, which only syncs to the customer when the job is linked + the write
 * succeeds. To catch the cases where that didn't happen, this card also
 * scans the customer's jobs (`jobs` prop) for vault credentials that aren't
 * in the canonical vault and offers a one-tap import.
 *
 * View mode masks sensitive values behind a per-row Eye toggle so showing
 * the screen to a customer doesn't flash every credential at once. Edit
 * mode exposes plain inputs for add / edit / remove.
 */

import { useState, useEffect, useMemo } from 'react';
import { Key, Wifi, Shield, Eye, EyeOff, Plus, Trash2, Copy, Check, Pencil, Loader2, Sparkles } from 'lucide-react';
import { Customer, Job, Vault, VaultItem } from '@/types';
import { updateCustomer } from '@/services/customer.service';

interface SiteVaultCardProps {
  customer: Customer;
  /**
   * The customer's jobs (already loaded by CustomerDetailModal, matched by
   * customerId AND by name). Scanned for vault credentials a tech captured
   * at completion that never synced to the customer record.
   */
  jobs?: Job[];
  /** Called with the locally-merged customer after a successful save. */
  onSaved?: (updated: Customer) => void;
}

/** Pull a normalised Vault out of the customer, folding in legacy WiFi fields. */
function readVault(customer: Customer): Vault {
  if (customer.vault) {
    return {
      ssid: customer.vault.ssid || '',
      pass: customer.vault.pass || '',
      other: customer.vault.other ? [...customer.vault.other] : [],
    };
  }
  // Migrate legacy flat fields into the vault shape so the office can edit
  // them in one place going forward.
  const other: VaultItem[] = [];
  if (customer.routerLogin || customer.routerPassword) {
    other.push({
      label: 'Router Login',
      value: customer.routerPassword
        ? `${customer.routerLogin || ''} / ${customer.routerPassword}`.trim()
        : customer.routerLogin || '',
    });
  }
  return {
    ssid: customer.wifiNetwork || '',
    pass: customer.wifiPassword || '',
    other,
  };
}

function vaultHasContent(v: Vault): boolean {
  return !!(v.ssid || v.pass || v.other.some((i) => i.label || i.value));
}

/**
 * Scan the customer's jobs for vault credentials that aren't already in the
 * canonical vault. Returns the WiFi pair from the most recent job that
 * differs from the saved one, plus any "other" credentials not already saved.
 */
function collectJobVaultSuggestions(
  customer: Customer,
  jobs?: Job[],
): { wifi: { ssid: string; pass: string } | null; others: VaultItem[] } {
  const empty = { wifi: null, others: [] as VaultItem[] };
  if (!jobs || jobs.length === 0) return empty;

  const existing = readVault(customer);
  const existingWifiKey = `${existing.ssid.trim()}|${existing.pass.trim()}`;
  const seen = new Set(existing.other.map((o) => `${o.label.trim().toLowerCase()}|${o.value.trim()}`));

  // Newest first so the most recent WiFi wins.
  const sorted = [...jobs].sort((a, b) => {
    const ax = (a.completedAt || a.updatedAt || a.date || '') as string;
    const bx = (b.completedAt || b.updatedAt || b.date || '') as string;
    return bx.localeCompare(ax);
  });

  let wifi: { ssid: string; pass: string } | null = null;
  const others: VaultItem[] = [];

  for (const j of sorted) {
    const v = j.vault;
    if (!v) continue;
    const ssid = (v.ssid || '').trim();
    const pass = (v.pass || '').trim();
    if (!wifi && (ssid || pass) && `${ssid}|${pass}` !== existingWifiKey) {
      wifi = { ssid, pass };
    }
    (v.other || []).forEach((o) => {
      const value = (o.value || '').trim();
      if (!value) return;
      const key = `${(o.label || '').trim().toLowerCase()}|${value}`;
      if (seen.has(key)) return;
      seen.add(key);
      others.push({ label: (o.label || '').trim() || 'Credential', value });
    });
  }
  return { wifi, others };
}

export function SiteVaultCard({ customer, jobs, onSaved }: SiteVaultCardProps) {
  const stored = readVault(customer);
  const hasContent = vaultHasContent(stored);

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Per-row reveal map for view mode. Reset whenever the customer changes.
  const [revealed, setRevealed] = useState<Record<string, boolean>>({});
  const [copied, setCopied] = useState<string | null>(null);

  // Draft edit state.
  const [ssid, setSsid] = useState(stored.ssid);
  const [pass, setPass] = useState(stored.pass);
  const [other, setOther] = useState<VaultItem[]>(stored.other);

  // Credentials a tech captured on a job that never reached the vault.
  const suggestions = useMemo(
    () => collectJobVaultSuggestions(customer, jobs),
    [customer.id, customer.vault, customer.wifiNetwork, customer.wifiPassword, jobs],
  );
  const hasSuggestions = !!(suggestions.wifi || suggestions.others.length > 0);

  // Re-sync local state when navigating to a different customer (the modal
  // reuses this component across an in-modal customer nav stack).
  useEffect(() => {
    const next = readVault(customer);
    setSsid(next.ssid);
    setPass(next.pass);
    setOther(next.other);
    setEditing(false);
    setRevealed({});
    setError(null);
  }, [customer.id]);  

  const beginEdit = () => {
    const next = readVault(customer);
    setSsid(next.ssid);
    setPass(next.pass);
    setOther(next.other);
    setError(null);
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setError(null);
  };

  const addRow = () => setOther((prev) => [...prev, { label: '', value: '' }]);
  const updateRow = (idx: number, patch: Partial<VaultItem>) =>
    setOther((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const removeRow = (idx: number) =>
    setOther((prev) => prev.filter((_, i) => i !== idx));

  const persistVault = async (vault: Vault) => {
    if (!customer.id) return;
    setSaving(true);
    setError(null);
    try {
      await updateCustomer(customer.id, { vault });
      onSaved?.({ ...customer, vault });
      setSsid(vault.ssid);
      setPass(vault.pass);
      setOther(vault.other);
      setEditing(false);
    } catch (err) {
      console.error('[SiteVaultCard] save failed:', err);
      setError('Could not save the vault. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    // Drop fully-empty credential rows; trim everything.
    const cleanedOther = other
      .map((it) => ({ label: it.label.trim(), value: it.value.trim() }))
      .filter((it) => it.label || it.value);
    await persistVault({ ssid: ssid.trim(), pass: pass.trim(), other: cleanedOther });
  };

  // Merge the job-sourced suggestions into the canonical vault and save.
  const importFromJobs = async () => {
    const base = readVault(customer);
    const mergedOther = [...base.other];
    const seen = new Set(mergedOther.map((o) => `${o.label.trim().toLowerCase()}|${o.value.trim()}`));
    suggestions.others.forEach((o) => {
      const key = `${o.label.trim().toLowerCase()}|${o.value.trim()}`;
      if (!seen.has(key)) {
        seen.add(key);
        mergedOther.push(o);
      }
    });
    await persistVault({
      ssid: base.ssid || suggestions.wifi?.ssid || '',
      pass: base.pass || suggestions.wifi?.pass || '',
      other: mergedOther,
    });
  };

  const copyValue = async (revealKey: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(revealKey);
      setTimeout(() => setCopied((c) => (c === revealKey ? null : c)), 1500);
    } catch {
      /* clipboard unavailable — silent */
    }
  };

  // ── Sensitive view row (masked value + reveal + copy) ──
  const ViewRow = ({
    revealKey,
    icon,
    label,
    value,
    sensitive = true,
  }: {
    revealKey: string;
    icon: React.ReactNode;
    label: string;
    value: string;
    sensitive?: boolean;
  }) => {
    const isRevealed = !sensitive || !!revealed[revealKey];
    return (
      <div className="flex items-center gap-3 group">
        {icon}
        <div className="min-w-0 flex-1">
          <div className="text-xs text-white/40">{label}</div>
          <div className="text-white/85 font-mono select-text break-all">
            {isRevealed ? value : '••••••••••'}
          </div>
        </div>
        <button
          onClick={() => copyValue(revealKey, value)}
          className="p-1.5 rounded text-white/60 hover:text-purple-300 hover:bg-purple-500/10 transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
          title={`Copy ${label.toLowerCase()}`}
          aria-label={`Copy ${label}`}
        >
          {copied === revealKey ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
        </button>
        {sensitive && (
          <button
            onClick={() => setRevealed((prev) => ({ ...prev, [revealKey]: !prev[revealKey] }))}
            className="p-1.5 rounded text-white/60 hover:text-purple-300 hover:bg-purple-500/10 transition-colors"
            title={isRevealed ? `Hide ${label.toLowerCase()}` : `Reveal ${label.toLowerCase()}`}
            aria-label={isRevealed ? `Hide ${label}` : `Reveal ${label}`}
          >
            {isRevealed ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        )}
      </div>
    );
  };

  // Human summary of what the import banner will add.
  const suggestionSummary = [
    suggestions.wifi ? 'WiFi' : null,
    suggestions.others.length > 0
      ? `${suggestions.others.length} credential${suggestions.others.length > 1 ? 's' : ''}`
      : null,
  ]
    .filter(Boolean)
    .join(' + ');

  return (
    <div className="bg-purple-500/5 rounded-lg border border-purple-500/20 p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Key className="w-5 h-5 text-purple-400" />
            Site Vault
          </h3>
          <p className="text-xs text-white/40 mt-0.5">Passwords &amp; access for this site</p>
        </div>
        {!editing && (
          <div className="flex items-center gap-2">
            {hasContent && (
              <span className="text-[10px] uppercase tracking-wider text-white/40 hidden sm:inline">
                Tap eye to reveal
              </span>
            )}
            <button
              onClick={beginEdit}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-purple-200 bg-purple-500/10 hover:bg-purple-500/20 border border-purple-500/30 transition-colors"
            >
              <Pencil className="w-3.5 h-3.5" />
              {hasContent ? 'Edit' : 'Add'}
            </button>
          </div>
        )}
      </div>

      {/* Recovery banner — credentials a tech captured on a job that never
          synced to the customer record. */}
      {!editing && hasSuggestions && (
        <div className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3">
          <div className="flex items-start gap-2.5">
            <Sparkles className="w-4 h-4 text-amber-300 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-amber-100 font-medium">Captured by a tech on a job</p>
              <p className="text-xs text-amber-200/70 mt-0.5">
                {suggestionSummary} from this customer's job{suggestions.wifi?.ssid ? ` · SSID “${suggestions.wifi.ssid}”` : ''}{' '}
                isn't in the Site Vault yet.
              </p>
            </div>
            <button
              onClick={importFromJobs}
              disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-amber-50 bg-amber-500/30 hover:bg-amber-500/40 border border-amber-400/40 transition-colors disabled:opacity-50 flex-shrink-0"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              Add to vault
            </button>
          </div>
        </div>
      )}

      {/* ── VIEW MODE ── */}
      {!editing && (
        hasContent ? (
          <div className="space-y-3">
            {stored.ssid && (
              <ViewRow
                revealKey="ssid"
                icon={<Wifi className="w-4 h-4 text-purple-400 flex-shrink-0" />}
                label="WiFi Network"
                value={stored.ssid}
                sensitive={false}
              />
            )}
            {stored.pass && (
              <ViewRow
                revealKey="pass"
                icon={<Shield className="w-4 h-4 text-purple-400 flex-shrink-0" />}
                label="WiFi Password"
                value={stored.pass}
              />
            )}
            {stored.other.map((item, idx) => (
              <ViewRow
                key={idx}
                revealKey={`other-${idx}`}
                icon={<Key className="w-4 h-4 text-purple-400 flex-shrink-0" />}
                label={item.label || 'Credential'}
                value={item.value}
              />
            ))}
          </div>
        ) : (
          !hasSuggestions && (
            <p className="text-sm text-white/50">
              No site passwords saved yet. Tap <span className="text-purple-200 font-medium">Add</span> to store WiFi,
              gate / alarm codes, router admin, or portal logins for this site.
            </p>
          )
        )
      )}

      {/* ── EDIT MODE ── */}
      {editing && (
        <div className="space-y-4">
          {/* WiFi pair */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-1">WiFi Network</label>
              <input
                value={ssid}
                onChange={(e) => setSsid(e.target.value)}
                placeholder="SSID"
                className="w-full px-3 py-2 bg-white/5 border border-white/15 rounded-lg text-sm text-white placeholder-white/30 font-mono focus:outline-none focus:ring-2 focus:ring-purple-500/40"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-1">WiFi Password</label>
              <input
                value={pass}
                onChange={(e) => setPass(e.target.value)}
                placeholder="Password"
                className="w-full px-3 py-2 bg-white/5 border border-white/15 rounded-lg text-sm text-white placeholder-white/30 font-mono focus:outline-none focus:ring-2 focus:ring-purple-500/40"
              />
            </div>
          </div>

          {/* Other credentials */}
          <div className="space-y-2">
            <div className="text-xs font-semibold text-white/50 uppercase tracking-wider">Other credentials</div>
            {other.length === 0 && (
              <p className="text-xs text-white/40">e.g. Gate code · Alarm code · Router admin · NBN portal login</p>
            )}
            {other.map((item, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <input
                  value={item.label}
                  onChange={(e) => updateRow(idx, { label: e.target.value })}
                  placeholder="Label"
                  className="w-1/3 px-3 py-2 bg-white/5 border border-white/15 rounded-lg text-sm text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-purple-500/40"
                />
                <input
                  value={item.value}
                  onChange={(e) => updateRow(idx, { value: e.target.value })}
                  placeholder="Value"
                  className="flex-1 px-3 py-2 bg-white/5 border border-white/15 rounded-lg text-sm text-white placeholder-white/30 font-mono focus:outline-none focus:ring-2 focus:ring-purple-500/40"
                />
                <button
                  onClick={() => removeRow(idx)}
                  className="p-2 rounded-lg text-white/50 hover:text-red-300 hover:bg-red-500/10 transition-colors flex-shrink-0"
                  title="Remove credential"
                  aria-label="Remove credential"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            <button
              onClick={addRow}
              className="flex items-center gap-1.5 text-sm text-purple-200 hover:text-purple-100 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add credential
            </button>
          </div>

          {error && <p className="text-sm text-red-300">{error}</p>}

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              onClick={cancelEdit}
              disabled={saving}
              className="px-4 py-2 rounded-lg text-sm text-white/70 hover:text-white hover:bg-white/5 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-purple-500/80 hover:bg-purple-500 transition-colors disabled:opacity-50"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {saving ? 'Saving…' : 'Save vault'}
            </button>
          </div>
        </div>
      )}

      {error && !editing && <p className="text-sm text-red-300 mt-3">{error}</p>}
    </div>
  );
}
